// Wiki + UI faction registry. The protocol-side `FactionId` and `FACTION_IDS`
// constants are the source of truth for the *type* and the on-wire ordering;
// this module holds the client-side display data: name, palette key, icon key,
// and the (currently empty) passive + powers definitions per faction.
//
// See `directives/specs/FACTIONS.md` for the framework. Powers + passives are
// placeholder stubs for the scaffolding pass — they exist so the UI can render
// 3 power cards and a passive line per faction. Real content lands faction by
// faction in follow-up sessions.

import type { FactionId } from "@towerhold/protocol";

export type PassiveDef = {
  /** Stable id — used internally for telemetry / addressing. */
  id: string;
  /** Display name (e.g., "Ayshera Blessing"). */
  name: string;
  /** Markdown-flavored description following the format in FACTIONS.md.
   *  Empty string for placeholders ("Coming soon" rendered by the UI). */
  description: string;
};

export type PowerDef = {
  /** Stable id — also the tech-node id when ready to wire to research. */
  id: string;
  /** Display name (e.g., "Overgrowth"). */
  name: string;
  /** Markdown-flavored description. Empty string for placeholders. */
  description: string;
};

export type FactionDef = {
  id: FactionId;
  /** Display name shown on the multichoice card and "Path:" banner. */
  name: string;
  /** One-line descriptive flavor — shown on the multichoice circles' hover
   *  tooltip. Theme + light hint at the faction's mechanical bent. Third-person
   *  per `feedback-no-second-person-in-wiki`. */
  tooltip: string;
  /** Key into apps/client/src/art/palettes.ts CANONICAL — drives card accents,
   *  banner borders, and picked-tier power-card tints. */
  paletteKey: string;
  /** Key into the icon-slot system (UNIT_DRAWS / TOWER_DRAWS / ABSTRACT_DRAWS).
   *  Faction icons land in ABSTRACT_DRAWS when art is committed. Empty until
   *  then — UI falls back to the first letter of the name. */
  iconKey: string;
  /** Passives (free, applied on faction pick). Length 1 / 2 / 3 per the
   *  blueprint's "1 big OR 2 medium OR 3 small" rule. Empty array allowed for
   *  passive-less factions. */
  passives: PassiveDef[];
  /** Three powers, individually researchable for 1 tech point each. The
   *  technology UI shows 3 cards per tier and expects exactly 3 entries. */
  powers: [PowerDef, PowerDef, PowerDef];
};

const placeholderPassive = (factionId: FactionId): PassiveDef => ({
  id: `${factionId}_passive`,
  name: "Coming soon",
  description: "",
});

const placeholderPower = (factionId: FactionId, n: 1 | 2 | 3): PowerDef => ({
  id: `${factionId}_power_${n}`,
  name: "Coming soon",
  description: "",
});

const placeholderPowers = (factionId: FactionId): [PowerDef, PowerDef, PowerDef] => [
  placeholderPower(factionId, 1),
  placeholderPower(factionId, 2),
  placeholderPower(factionId, 3),
];

export const FACTIONS: Record<FactionId, FactionDef> = {
  nature: {
    id: "nature",
    name: "Nature",
    tooltip: "Channels the forces of nature, enhancing the health of recruits.",
    paletteKey: "factionNature",
    iconKey: "",
    passives: [placeholderPassive("nature")],
    powers: placeholderPowers("nature"),
  },
  ice: {
    id: "ice",
    name: "Ice",
    tooltip: "Calls down the chill of winter, slowing the press of attack.",
    paletteKey: "factionIce",
    iconKey: "",
    passives: [placeholderPassive("ice")],
    powers: placeholderPowers("ice"),
  },
  knight: {
    id: "knight",
    name: "Knight",
    tooltip: "Stands with a sworn order of arms — armor, discipline, the weight of duty.",
    paletteKey: "factionKnight",
    iconKey: "",
    passives: [placeholderPassive("knight")],
    powers: placeholderPowers("knight"),
  },
};
